/**
 * A minimal HTML start/end tag tokenizer for the band swap. Pure text, no DOM,
 * so it runs anywhere (no DOMParser needed).
 *
 * It reports ONLY real element tags. Text nodes, comments (`<!-- -->`),
 * doctype/`<!…>`, `<?…>` processing instructions, and the CONTENTS of raw-text
 * elements (`script`, `style`, `textarea`, `title`) are skipped. That is what
 * keeps a shortcode like `[gravityform id="1"]`, an `id=` inside a `<script>`,
 * or a `<section>` inside a comment from ever being seen as an element or an
 * element attribute — every id/class collection and every rewrite the band swap
 * makes goes through these tokens, spliced by byte offset, so all other bytes
 * (text, shortcodes, comments, script/style text, whitespace, quoting) stay
 * byte-identical.
 */

export type TagAttr = {
  /** Attribute name exactly as written. */
  name: string;
  /** Attribute name lowercased, for case-insensitive matching. */
  nameLower: string;
  /** Raw value bytes (between the quotes, or the unquoted run); null when the attribute has no value. */
  value: string | null;
  /** Byte offset where the value starts (just inside the quote when quoted); -1 when valueless. */
  valueStart: number;
  /** Byte offset just past the value; -1 when valueless. */
  valueEnd: number;
  /** Quote char `"` or `'`, or '' when the value is unquoted or the attribute is valueless. */
  quote: string;
};

export type StartTag = {
  kind: 'start';
  /** Lowercased tag name. */
  name: string;
  /** Byte offset of the leading `<`. */
  start: number;
  /** Byte offset just past the closing `>`. */
  end: number;
  attrs: TagAttr[];
  selfClosing: boolean;
};
export type EndTag = { kind: 'end'; name: string; start: number; end: number };
export type Tag = StartTag | EndTag;

const RAW_TEXT = new Set(['script', 'style', 'textarea', 'title']);
const isAsciiAlpha = (c: string): boolean => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z');
const isNameChar = (c: string): boolean => c !== undefined && !/[\s/>=]/.test(c);

/** Parse a start tag beginning at the `<` at `lt`. */
function parseStartTag(html: string, lt: number): StartTag {
  const n = html.length;
  let i = lt + 1;
  const nameStart = i;
  while (i < n && !/[\s/>]/.test(html[i])) i += 1;
  const name = html.slice(nameStart, i).toLowerCase();
  const attrs: TagAttr[] = [];
  let selfClosing = false;
  while (i < n) {
    while (i < n && /\s/.test(html[i])) i += 1;
    if (i >= n) break;
    const c = html[i];
    if (c === '>') { i += 1; break; }
    if (c === '/') {
      if (html[i + 1] === '>') { selfClosing = true; i += 2; break; }
      i += 1; // stray slash between attributes
      continue;
    }
    const anStart = i;
    while (i < n && isNameChar(html[i])) i += 1;
    if (i === anStart) { i += 1; continue; } // no progress (e.g. a lone '='); skip a byte
    const aname = html.slice(anStart, i);
    let k = i;
    while (k < n && /\s/.test(html[k])) k += 1;
    if (html[k] === '=') {
      k += 1;
      while (k < n && /\s/.test(html[k])) k += 1;
      const q = html[k];
      if (q === '"' || q === "'") {
        const vStart = k + 1;
        const close = html.indexOf(q, vStart);
        const vEnd = close < 0 ? n : close;
        attrs.push({ name: aname, nameLower: aname.toLowerCase(), value: html.slice(vStart, vEnd), valueStart: vStart, valueEnd: vEnd, quote: q });
        i = close < 0 ? n : close + 1;
      } else {
        const vStart = k;
        let m = k;
        while (m < n && !/[\s>]/.test(html[m])) m += 1;
        attrs.push({ name: aname, nameLower: aname.toLowerCase(), value: html.slice(vStart, m), valueStart: vStart, valueEnd: m, quote: '' });
        i = m;
      }
    } else {
      attrs.push({ name: aname, nameLower: aname.toLowerCase(), value: null, valueStart: -1, valueEnd: -1, quote: '' });
      i = k;
    }
  }
  return { kind: 'start', name, start: lt, end: i, attrs, selfClosing };
}

/** Byte offset of the `<` of the matching `</name` end tag from `from`; end of string if none. */
function rawTextEnd(html: string, name: string, from: number): number {
  const lower = html.toLowerCase();
  const needle = `</${name}`;
  let i = from;
  for (;;) {
    const at = lower.indexOf(needle, i);
    if (at < 0) return html.length;
    const after = html[at + needle.length];
    if (after === undefined || /[\s/>]/.test(after)) return at;
    i = at + needle.length;
  }
}

/** Every real element start/end tag of `html`, in document order. */
export function tokenizeTags(html: string): Tag[] {
  const out: Tag[] = [];
  const n = html.length;
  let i = 0;
  while (i < n) {
    const lt = html.indexOf('<', i);
    if (lt < 0) break;
    const c = html[lt + 1];
    if (c === '!') {
      if (html.startsWith('<!--', lt)) {
        const end = html.indexOf('-->', lt + 4);
        i = end < 0 ? n : end + 3;
      } else {
        const gt = html.indexOf('>', lt + 2);
        i = gt < 0 ? n : gt + 1;
      }
      continue;
    }
    if (c === '?') {
      const gt = html.indexOf('>', lt + 2);
      i = gt < 0 ? n : gt + 1;
      continue;
    }
    if (c === '/') {
      if (!isAsciiAlpha(html[lt + 2] ?? '')) { i = lt + 1; continue; }
      let j = lt + 2;
      while (j < n && !/[\s/>]/.test(html[j])) j += 1;
      const name = html.slice(lt + 2, j).toLowerCase();
      const gt = html.indexOf('>', j);
      const end = gt < 0 ? n : gt + 1;
      out.push({ kind: 'end', name, start: lt, end });
      i = end;
      continue;
    }
    if (!isAsciiAlpha(c ?? '')) { i = lt + 1; continue; } // a bare '<' in text
    const tag = parseStartTag(html, lt);
    out.push(tag);
    i = tag.end;
    if (RAW_TEXT.has(tag.name) && !tag.selfClosing) i = rawTextEnd(html, tag.name, i);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Attribute-level id-reference rules, shared by rewriting and dangling checks.
// ---------------------------------------------------------------------------

/** Attributes whose value is a whitespace-separated list of id references. */
export const TOKEN_LIST_REF_ATTRS = new Set([
  'for',
  'form',
  'list',
  'headers',
  'aria-labelledby',
  'aria-describedby',
  'aria-controls',
  'aria-owns',
  'aria-activedescendant',
  'aria-errormessage',
  'aria-flowto',
  'aria-details',
]);
/** Attributes whose value is a single id reference. */
export const SINGLE_ID_REF_ATTRS = new Set(['popovertarget']);
/** Attributes whose `#frag` value is an id reference. */
export const FRAGMENT_REF_ATTRS = new Set(['href', 'xlink:href']);

export type TokenMap = (token: string) => string;
export type MarkupRewrite = {
  /** Map a class token to its (possibly renamed) form; return it unchanged when not owned. */
  renameClass: TokenMap;
  /** Map an id token (used for `id=""` and for id references); unchanged when unknown. */
  renameId: TokenMap;
  /** Optional: map an `@keyframes` name inside `style` attribute `animation` values. */
  renameKeyframe?: TokenMap;
};

// The quote around `url("#id")` can be a real quote char OR its HTML entity, as
// written inside a same-quoted attribute: `fill="url(&quot;#paint&quot;)"`. Accept
// the entity forms too (case-insensitive via the /i flag, leading zeros allowed in
// numeric refs) and keep whatever was captured verbatim on rewrite. The trailing
// empty alternative lets the group match the unquoted `url(#id)` form as well.
// ponytail: `\1` pairs open/close by exact text, so a mixed-CASE same-type pair
// (`&QUOT;…&quot;`) would normalise to the opening form — not a shape any tool emits.
const QUOTE = String.raw`&quot;|&#0*34;|&#x0*22;|&apos;|&#0*39;|&#x0*27;|['"]|`;
const URL_ID = new RegExp(String.raw`url\(\s*(${QUOTE})#([\w-]+)\1\s*\)`, 'gi');

/** Map every whitespace-separated token, preserving the exact separators. */
function rewriteTokenList(value: string, map: TokenMap): string {
  return value
    .split(/(\s+)/)
    .map((part) => (part && !/\s/.test(part) ? map(part) : part))
    .join('');
}

/** Map a single-token value, preserving surrounding whitespace. */
function rewriteSingle(value: string, map: TokenMap): string {
  const t = value.trim();
  if (!t) return value;
  const lead = value.slice(0, value.indexOf(t));
  const trail = value.slice(value.indexOf(t) + t.length);
  return lead + map(t) + trail;
}

/** Map the `#frag` of a fragment-reference value. */
function rewriteFragment(value: string, map: TokenMap): string {
  if (value[0] !== '#') return value;
  return '#' + map(value.slice(1));
}

/** Map every `url(#id)` / `url("#id")` / `url('#id')`, keeping the quote. */
function rewriteUrlIds(value: string, map: TokenMap): string {
  return value.replace(URL_ID, (_whole, q: string, id: string) => `url(${q}#${map(id)}${q})`);
}

/** Map `@keyframes` names inside `animation`/`animation-name` declarations of a style attribute. */
function rewriteStyleAnimations(value: string, map: TokenMap): string {
  return value.replace(/\banimation(-name)?\s*:\s*([^;]+)/gi, (_whole, suffix: string, val: string) => {
    const next = val
      .split(/([\s,]+)/)
      .map((word) => (word && !/[\s,]/.test(word) ? map(word) : word))
      .join('');
    return `animation${suffix ?? ''}:${next}`;
  });
}

/** The new value for one attribute under a rewrite, or the value unchanged. */
function rewriteAttrValue(nameLower: string, value: string, rw: MarkupRewrite): string {
  if (nameLower === 'class') return rewriteTokenList(value, rw.renameClass);
  if (nameLower === 'id') return rewriteSingle(value, rw.renameId);
  let out = value;
  if (FRAGMENT_REF_ATTRS.has(nameLower)) out = rewriteFragment(out, rw.renameId);
  else if (TOKEN_LIST_REF_ATTRS.has(nameLower)) out = rewriteTokenList(out, rw.renameId);
  else if (SINGLE_ID_REF_ATTRS.has(nameLower)) out = rewriteSingle(out, rw.renameId);
  out = rewriteUrlIds(out, rw.renameId); // url(#id) can hide in style, fill, clip-path, mask, filter, marker-*
  if (nameLower === 'style' && rw.renameKeyframe) out = rewriteStyleAnimations(out, rw.renameKeyframe);
  return out;
}

/**
 * Rewrite id/class names and every kind of id reference in `html`, touching ONLY
 * real element attribute value spans (spliced by offset). Every other byte stays
 * identical. Quote chars are kept; an unquoted value stays unquoted (the renamed
 * tokens are safe css identifiers).
 */
export function applyMarkupRewrite(html: string, rw: MarkupRewrite): string {
  const edits: Array<{ start: number; end: number; text: string }> = [];
  for (const tag of tokenizeTags(html)) {
    if (tag.kind !== 'start') continue;
    for (const attr of tag.attrs) {
      if (attr.value === null) continue;
      const next = rewriteAttrValue(attr.nameLower, attr.value, rw);
      if (next !== attr.value) edits.push({ start: attr.valueStart, end: attr.valueEnd, text: next });
    }
  }
  edits.sort((a, b) => b.start - a.start);
  let out = html;
  for (const e of edits) out = out.slice(0, e.start) + e.text + out.slice(e.end);
  return out;
}

export type IdRef = { id: string; attr: string; sample: string; start: number };

/** Every id REFERENCE carried by element attributes (fragment, token-list, single, and url(#…)). */
export function collectAttrRefs(html: string): IdRef[] {
  const out: IdRef[] = [];
  for (const tag of tokenizeTags(html)) {
    if (tag.kind !== 'start') continue;
    for (const attr of tag.attrs) {
      if (attr.value === null) continue;
      const v = attr.value;
      const push = (id: string, sample: string) => { if (id) out.push({ id, attr: attr.nameLower, sample, start: attr.valueStart }); };
      if (FRAGMENT_REF_ATTRS.has(attr.nameLower) && v[0] === '#') push(v.slice(1), `${attr.name}="${v}"`);
      else if (TOKEN_LIST_REF_ATTRS.has(attr.nameLower)) for (const t of v.split(/\s+/)) push(t, `${attr.name}="${v}"`);
      else if (SINGLE_ID_REF_ATTRS.has(attr.nameLower)) push(v.trim(), `${attr.name}="${v}"`);
      for (const m of v.matchAll(URL_ID)) push(m[2], `url(#${m[2]})`);
    }
  }
  return out;
}
